// Keeps transparent recovery state isolated to one API client. Cooldowns only
// move later, and owner tokens prevent concurrent retries from releasing one
// another's claims.

import { ApiError } from '@libraries/typescript/api_client/error';
import {
    type FetchResponse,
    type HttpMethod,
} from '@libraries/typescript/api_client/types';

const GLOBAL_OVERLOAD_HEADER = 'X-Phoebe-Global-Overload';
const RATE_LIMITED_HEADER = 'X-Phoebe-Rate-Limited';
// The server emits at most 180 seconds. Five minutes leaves protocol headroom
// without letting a bad header disable the client for the rest of a session.
const MAX_RETRY_AFTER_DELAY_MS = 5 * 60 * 1_000;
// Bound automatic recovery so one logical request cannot remain pending
// indefinitely across a chain of server-directed waits.
const RETRY_WAIT_BUDGET_MS = 60_000;

type TransparentRateLimitSource = 'principal' | 'global-overload';

type GlobalOverloadCooldown = {
    readonly retryAtMs: number;
    readonly status: number;
    readonly message: string;
};

export type RateLimitCooldown = {
    readonly retryAtMs: number;
    readonly scope: string;
    readonly message: string;
};

export type RateLimitRetryOwner = {
    readonly source: TransparentRateLimitSource;
    readonly routeKey: string;
    readonly ownerToken: symbol;
};

type RecordedRateLimitResponse = {
    readonly source: TransparentRateLimitSource | null;
    readonly isGlobalOverload: boolean;
    readonly rateLimitedScope: string | undefined;
    readonly rateLimitedRetryAtMs: number | undefined;
    readonly cooldownArmed: boolean;
};

type RecordRateLimitResponseOptions = {
    readonly response: FetchResponse;
    readonly retryAtMs: number | undefined;
    readonly routeKey: string;
    readonly message: string;
};

type FinalizeTransparentRateLimitErrorOptions = {
    readonly error: ApiError;
    readonly resilient: boolean;
    readonly method: HttpMethod | undefined;
    readonly signal: AbortSignal | undefined;
    readonly url: string;
    readonly failureMode: 'silent' | 'notify';
    readonly onExhausted:
        | ((context: {
              readonly error: ApiError;
              readonly method: HttpMethod;
              readonly signal?: AbortSignal;
              readonly url: string;
          }) => void | Promise<void>)
        | undefined;
    readonly createCancellationError: () => ApiError;
    readonly onPresentationError: (error: unknown, method: HttpMethod) => void;
};

type RateLimitRecovery = {
    readonly getActiveGlobalCooldown: () => GlobalOverloadCooldown | null;
    readonly getActivePrincipalCooldown: () => RateLimitCooldown | null;
    readonly getActiveRouteCooldown: (
        routeKey: string,
    ) => RateLimitCooldown | null;
    readonly recordResponseCooldowns: (
        options: RecordRateLimitResponseOptions,
    ) => RecordedRateLimitResponse;
    readonly getPendingRetryAtMs: (
        pendingRetry: RateLimitRetryOwner,
    ) => number | undefined;
    readonly hasRetryOwner: (
        source: TransparentRateLimitSource,
        routeKey: string,
    ) => boolean;
    readonly claimRetryOwner: (
        source: TransparentRateLimitSource,
        routeKey: string,
    ) => RateLimitRetryOwner | undefined;
    readonly releaseRetryOwner: (owner: RateLimitRetryOwner) => void;
};

type RetryWaitBudget = {
    readonly fits: (retryAtMs: number) => boolean;
    readonly admit: (retryAtMs: number) => boolean;
};

function classifyTransparentRateLimit(
    response: FetchResponse,
): TransparentRateLimitSource | null {
    if (response.headers.has(GLOBAL_OVERLOAD_HEADER)) {
        return 'global-overload';
    }
    if (response.headers.has(RATE_LIMITED_HEADER)) {
        return 'principal';
    }
    return null;
}

export function getRetryAfterDelayMs(response: FetchResponse): number | null {
    const retryAfter = response.headers.get('Retry-After')?.trim();
    if (!retryAfter) {
        return null;
    }

    const seconds = Number(retryAfter);
    if (Number.isFinite(seconds) && seconds >= 0) {
        const delayMs = seconds * 1_000;
        return Number.isFinite(delayMs)
            ? Math.min(delayMs, MAX_RETRY_AFTER_DELAY_MS)
            : null;
    }

    const retryAtMs = Date.parse(retryAfter);
    if (!Number.isFinite(retryAtMs)) {
        return null;
    }
    return Math.min(
        Math.max(0, retryAtMs - Date.now()),
        MAX_RETRY_AFTER_DELAY_MS,
    );
}

export function isTransparentRateLimitRecoveryEligible(
    method: string | undefined,
    idempotent: boolean | undefined,
): boolean {
    return (
        method !== undefined &&
        (method === 'GET' || method === 'HEAD' || idempotent === true)
    );
}

export async function finalizeTransparentRateLimitError({
    error,
    resilient,
    method,
    signal,
    url,
    failureMode,
    onExhausted,
    createCancellationError,
    onPresentationError,
}: FinalizeTransparentRateLimitErrorOptions): Promise<never> {
    if (
        !resilient ||
        method === undefined ||
        error.transparentRateLimitSource === undefined
    ) {
        throw error;
    }
    if (signal?.aborted) {
        throw createCancellationError();
    }
    if (failureMode === 'notify' && onExhausted !== undefined) {
        try {
            await onExhausted({ error, method, signal, url });
        } catch (callbackError: unknown) {
            try {
                onPresentationError(callbackError, method);
            } catch {
                // Presentation diagnostics cannot replace the request failure
                // that callers use for recovery.
            }
        }
    }
    if (signal?.aborted) {
        throw createCancellationError();
    }
    throw error;
}

export function createRateLimitRecovery(): RateLimitRecovery {
    // Shared by this client instance so one explicit server cooldown prevents
    // independent SWR consumers from immediately replacing a reconnect herd
    // with a Retry-After herd. Mutations are never blocked by this read-path
    // circuit breaker.
    let globalCooldown: GlobalOverloadCooldown | null = null;
    let principalCooldown: RateLimitCooldown | null = null;
    const routeCooldowns = new Map<string, RateLimitCooldown>();
    const retryOwners = new Map<string, symbol>();

    function getActiveGlobalCooldown(): GlobalOverloadCooldown | null {
        if (globalCooldown !== null && Date.now() >= globalCooldown.retryAtMs) {
            globalCooldown = null;
        }
        return globalCooldown;
    }

    function replaceGlobalCooldown(next: GlobalOverloadCooldown): void {
        if (
            globalCooldown === null ||
            next.retryAtMs >= globalCooldown.retryAtMs
        ) {
            globalCooldown = next;
        }
    }

    function getActivePrincipalCooldown(): RateLimitCooldown | null {
        if (
            principalCooldown !== null &&
            Date.now() >= principalCooldown.retryAtMs
        ) {
            principalCooldown = null;
        }
        return principalCooldown;
    }

    function replacePrincipalCooldown(next: RateLimitCooldown): void {
        if (
            principalCooldown === null ||
            next.retryAtMs >= principalCooldown.retryAtMs
        ) {
            principalCooldown = next;
        }
    }

    function getActiveRouteCooldown(
        routeKey: string,
    ): RateLimitCooldown | null {
        const routeCooldown = routeCooldowns.get(routeKey);
        if (routeCooldown === undefined) {
            return null;
        }
        if (Date.now() >= routeCooldown.retryAtMs) {
            routeCooldowns.delete(routeKey);
            return null;
        }
        return routeCooldown;
    }

    function replaceRouteCooldown(
        routeKey: string,
        next: RateLimitCooldown,
    ): void {
        const nowMs = Date.now();
        for (const [cooldownKey, existingCooldown] of routeCooldowns) {
            if (nowMs >= existingCooldown.retryAtMs) {
                routeCooldowns.delete(cooldownKey);
            }
        }
        const existingCooldown = routeCooldowns.get(routeKey);
        if (
            existingCooldown === undefined ||
            next.retryAtMs >= existingCooldown.retryAtMs
        ) {
            routeCooldowns.set(routeKey, next);
        }
    }

    function recordResponseCooldowns({
        response,
        retryAtMs,
        routeKey,
        message,
    }: RecordRateLimitResponseOptions): RecordedRateLimitResponse {
        const source = classifyTransparentRateLimit(response);
        const isGlobalOverload =
            retryAtMs !== undefined && source === 'global-overload';
        let cooldownArmed = false;
        if (isGlobalOverload) {
            replaceGlobalCooldown({
                retryAtMs,
                status: response.status,
                message,
            });
            cooldownArmed = true;
        }

        const rateLimitedScope =
            response.status === 429
                ? response.headers.get(RATE_LIMITED_HEADER)?.trim() || undefined
                : undefined;
        const rateLimitedRetryAtMs =
            rateLimitedScope === undefined ? undefined : retryAtMs;
        if (
            rateLimitedScope !== undefined &&
            rateLimitedRetryAtMs !== undefined &&
            source === 'principal'
        ) {
            const rateLimitCooldown = {
                retryAtMs: rateLimitedRetryAtMs,
                scope: rateLimitedScope,
                message,
            } satisfies RateLimitCooldown;
            if (rateLimitedScope === 'user' || rateLimitedScope === 'org') {
                replacePrincipalCooldown(rateLimitCooldown);
            } else {
                replaceRouteCooldown(routeKey, rateLimitCooldown);
            }
            cooldownArmed = true;
        }

        return {
            source,
            isGlobalOverload,
            rateLimitedScope,
            rateLimitedRetryAtMs,
            cooldownArmed,
        };
    }

    function getPendingRetryAtMs(
        pendingRetry: RateLimitRetryOwner,
    ): number | undefined {
        if (pendingRetry.source === 'global-overload') {
            return getActiveGlobalCooldown()?.retryAtMs;
        }

        const activePrincipalCooldown = getActivePrincipalCooldown();
        const activeRouteCooldown = getActiveRouteCooldown(
            pendingRetry.routeKey,
        );
        if (activePrincipalCooldown === null) {
            return activeRouteCooldown?.retryAtMs;
        }
        if (activeRouteCooldown === null) {
            return activePrincipalCooldown.retryAtMs;
        }
        return Math.max(
            activePrincipalCooldown.retryAtMs,
            activeRouteCooldown.retryAtMs,
        );
    }

    function claimRetryOwner(
        source: TransparentRateLimitSource,
        routeKey: string,
    ): RateLimitRetryOwner | undefined {
        const ownerKey = `${source} ${routeKey}`;
        if (retryOwners.has(ownerKey)) {
            return undefined;
        }
        const ownerToken = Symbol(ownerKey);
        retryOwners.set(ownerKey, ownerToken);
        return { source, routeKey, ownerToken };
    }

    function hasRetryOwner(
        source: TransparentRateLimitSource,
        routeKey: string,
    ): boolean {
        return retryOwners.has(`${source} ${routeKey}`);
    }

    function releaseRetryOwner(owner: RateLimitRetryOwner): void {
        const ownerKey = `${owner.source} ${owner.routeKey}`;
        if (retryOwners.get(ownerKey) === owner.ownerToken) {
            retryOwners.delete(ownerKey);
        }
    }

    return {
        getActiveGlobalCooldown,
        getActivePrincipalCooldown,
        getActiveRouteCooldown,
        recordResponseCooldowns,
        getPendingRetryAtMs,
        hasRetryOwner,
        claimRetryOwner,
        releaseRetryOwner,
    };
}

export function createRetryWaitBudget(): RetryWaitBudget {
    let retryWaitStartedAtMs: number | undefined;

    function fits(retryAtMs: number): boolean {
        return (
            retryWaitStartedAtMs !== undefined &&
            retryAtMs <= retryWaitStartedAtMs + RETRY_WAIT_BUDGET_MS
        );
    }

    function admit(retryAtMs: number): boolean {
        retryWaitStartedAtMs ??= Date.now();
        return fits(retryAtMs);
    }

    return { fits, admit };
}

export function createRateLimitSuppressedApiError(
    cooldown: RateLimitCooldown,
    requestId: string,
): ApiError {
    return new ApiError(cooldown.message, 429, undefined, undefined, {
        rateLimitedScope: cooldown.scope,
        rateLimitedRetryAtMs: cooldown.retryAtMs,
        rateLimitSuppressed: true,
        requestId,
        transparentRateLimitSource: 'principal',
    });
}
